import { ShipmentModel, ValidationMessageModel } from '@data-layer/models/common';
import { enumTransportationMode } from '@data-layer/enums/entry-summaries';
import { EntrySummaryModel, InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { enumValidationMessageSeverity } from '@data-layer/enums/common';
import { Injectable } from '@angular/core';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { CurrenciesRepository } from '@data-layer/repositories';
import { EntrySummaryValidationHelper } from '@helpers/entry-summary-validation.helper';
import { CURRENCY_IS_NOT_AVAILABLE } from '@constants/entity-validation-messages';
import { CustomDateByUtcPipe } from '@shared/pipes-shared/pipes';
import { ManagementEntrySummaryModel } from '@data-layer/models/management';
import { DateHelper } from '@helpers/date.helper';
import { UtilsHelper } from '@helpers/utils-helper';

@Injectable({ providedIn: 'root' })
export class EntrySummaryService {
  constructor(private currenciesRepository: CurrenciesRepository, private customDatePipe: CustomDateByUtcPipe) {}

  public static modified(es: EntrySummaryModel | ManagementEntrySummaryModel): boolean {
    const sentToProviderDate = es.sentToProviderDate;
    if (!sentToProviderDate) {
      return true;
    }
    const sentToProviderDateMS: number = DateHelper.getTime(sentToProviderDate);
    const allLastModified: string[] = this.getAllLastModified(es);
    const allLastModifiedMS: number[] = allLastModified.map((date) => DateHelper.getTime(date));
    const maxLastModifiedMS: number = UtilsHelper.maxElement(allLastModifiedMS);
    return sentToProviderDateMS < maxLastModifiedMS;
  }

  public getShipmentValidationMessagesErrorData(
    shipment: ShipmentModel,
    transportationMode: enumTransportationMode,
  ): Observable<ValidationMessageModel[]> {
    const messages = EntrySummaryValidationHelper.getValidationMessages(shipment, transportationMode, false, ['SHIPMENTS'], true);
    return (
      shipment.invoices && shipment.invoices.length
        ? forkJoin(
            shipment.invoices.map((invoice) =>
              this.getInvoiceEnteredValueInDefaultCurrency(invoice, shipment.exportDate || DateHelper.now).pipe(
                map((enteredValue) => ({ enteredValue, invoice })),
              ),
            ),
          )
        : of([])
    ).pipe(
      map((data) => {
        data.forEach(({ enteredValue, invoice }) => {
          if (isNaN(enteredValue)) {
            messages.push({
              text: CURRENCY_IS_NOT_AVAILABLE,
              severity: enumValidationMessageSeverity.ERROR,
              params: {
                exportDate: this.customDatePipe.transform(shipment.exportDate),
                shipmentId: shipment.id,
                invoiceId: invoice.id,
                currency: invoice.currency,
              },
            });
          }
        });

        return messages;
      }),
    );
  }

  public getEntrySummaryEnteredValue(entrySummary: EntrySummaryModel): Observable<number> {
    return entrySummary && entrySummary.shipments && entrySummary.shipments.length
      ? forkJoin(entrySummary.shipments.map((shipment) => this.getShipmentEnteredValue(shipment))).pipe(
          map((values) => values.reduce((prev, curr) => prev + curr, 0)),
        )
      : of(0);
  }

  public getShipmentEnteredValue(shipment: ShipmentModel): Observable<number> {
    return shipment && shipment.invoices && shipment.invoices.length
      ? forkJoin(
          shipment.invoices.map((invoice) => this.getInvoiceEnteredValueInDefaultCurrency(invoice, shipment.exportDate || DateHelper.now)),
        ).pipe(map((values) => values.reduce((prev, curr) => prev + curr, 0)))
      : of(0);
  }

  public getInvoiceEnteredValueInDefaultCurrency(invoice: InvoiceModel, exportDate: string): Observable<number> {
    const enteredValue = this.getInvoiceEnteredValue(invoice);
    if (!invoice?.currency || invoice?.currency === DEFAULT_CURRENCY_CODE || enteredValue === 0) {
      return of(enteredValue);
    }

    return this.currenciesRepository.getRate(invoice.currency, exportDate).pipe(map((rate) => enteredValue * (rate ? rate.rate : NaN)));
  }

  private getInvoiceEnteredValue(invoice: InvoiceModel): number {
    return invoice?.lines && invoice?.lines?.length ? this.getSumEnteredValue(invoice?.lines) : 0;
  }

  private getSumEnteredValue(items: LineModel[]): number {
    return items.reduce((prev, curr) => prev + curr.value, 0);
  }

  private static getAllLastModified(obj: any): string[] {
    let result: string[] = [];
    if (typeof obj === 'object') {
      for (const field in obj) {
        if (!obj.hasOwnProperty(field)) {
          continue;
        }
        const fieldValue = obj[field];
        if (field === 'lastModified' && fieldValue) {
          result = UtilsHelper.concat<string>(result, fieldValue as string);
        }
        if (typeof fieldValue === 'object') {
          result = UtilsHelper.concat<string>(result, this.getAllLastModified(fieldValue));
        }
      }
    }
    return result;
  }
}
